/**
 * <s-device-filter> — filters the design row (blocks/series-switcher.liquid) by exact device.
 *
 * - Items carry data-device-keys (",key1,key2,") and data-device-urls ("key|url key|url").
 * - data-active-key on the host: the device rendered by Liquid (the viewed variant's device).
 * - A device radio (input[data-device-key], also inside the expanding list details[data-device-expand], which closes on pick) or dropdown (select[data-device-select]) changed inside the same <s-product-form>: filter to that key
 *   right away (the product-form partial refresh then re-renders the same state).
 * - A device chip (button[data-device-pick], snippets/device-option.liquid) for a device this design doesn't sell:
 *   mark it pressed, clear the device radio, disable add to cart, show the hint, and filter to that key.
 * - Visible items link to the variant for the active device.
 */
class SDeviceFilter extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    document.addEventListener('change', this.#handleChange, { signal: this.#ac.signal });
    document.addEventListener('click', this.#handlePick, { signal: this.#ac.signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  get #scope() {
    return this.closest('s-product-form') || document;
  }

  #filter(key) {
    this.dataset.activeKey = key;
    const needle = `,${key},`;
    for (const item of this.querySelectorAll('[data-device-keys]')) {
      if (!(item instanceof HTMLElement)) continue;
      const matches = (item.dataset.deviceKeys || '').includes(needle);
      item.hidden = !matches;
      if (!matches) continue;
      const pair = (item.dataset.deviceUrls || '').split(' ').find((entry) => entry.startsWith(`${key}|`));
      const link = item.querySelector('a');
      if (pair && link) link.setAttribute('href', pair.slice(key.length + 1));
    }
    const list = this.querySelector('ul');
    if (list) list.scrollLeft = 0;
  }

  #enableSubmit(form, enabled) {
    for (const submit of form?.querySelectorAll('[name="add"], .shopify-payment-button') ?? []) {
      if (submit instanceof HTMLButtonElement) {
        submit.disabled = !enabled;
        if (enabled) submit.removeAttribute('aria-disabled');
        else submit.setAttribute('aria-disabled', 'true');
      } else if (submit instanceof HTMLElement) {
        submit.hidden = !enabled;
      }
    }
  }

  #selectDevice(control, data) {
    const fieldset = control.closest('[data-device-fieldset]');
    for (const chip of fieldset?.querySelectorAll('button[data-device-pick]') ?? []) chip.setAttribute('aria-pressed', 'false');
    const message = fieldset?.querySelector('[data-device-missing-msg]');
    if (message instanceof HTMLElement) message.hidden = true;
    const selected = fieldset?.querySelector('[data-device-selected]');
    if (selected) selected.textContent = data.deviceLabel || '';
    fieldset?.querySelector('details[data-device-expand]')?.removeAttribute('open');
    this.#enableSubmit(control.closest('form'), true);
    this.#filter(data.deviceKey || '');
  }

  #pickMissing(control, data) {
    const fieldset = control.closest('[data-device-fieldset]');
    const selected = fieldset?.querySelector('[data-device-selected]');
    if (selected) selected.textContent = data.deviceLabel || '';
    const message = fieldset?.querySelector('[data-device-missing-msg]');
    if (message instanceof HTMLElement) {
      message.textContent = data.deviceMsg || '';
      message.hidden = false;
    }
    fieldset?.querySelector('details[data-device-expand]')?.removeAttribute('open');
    this.#enableSubmit(control.closest('form'), false);
    this.#filter(data.deviceKey || '');
  }

  #handleChange = (event) => {
    const control = event.target;
    if (!this.#scope.contains(control)) return;
    if (control instanceof HTMLSelectElement && control.hasAttribute('data-device-select')) {
      const option = control.selectedOptions[0];
      if (!option) return;
      if (option.hasAttribute('data-device-pick')) this.#pickMissing(control, option.dataset);
      else this.#selectDevice(control, option.dataset);
      return;
    }
    if (!(control instanceof HTMLInputElement) || !control.dataset.deviceKey) return;
    this.#selectDevice(control, control.dataset);
  };

  #handlePick = (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('button[data-device-pick]');
    if (!(button instanceof HTMLElement) || !this.#scope.contains(button)) return;
    const fieldset = button.closest('[data-device-fieldset]');
    for (const chip of fieldset?.querySelectorAll('button[data-device-pick]') ?? []) {
      chip.setAttribute('aria-pressed', String(chip === button));
    }
    for (const radio of fieldset?.querySelectorAll('input[type="radio"]') ?? []) {
      if (radio instanceof HTMLInputElement) radio.checked = false;
    }
    this.#pickMissing(button, button.dataset);
  };
}

if (!customElements.get('s-device-filter')) customElements.define('s-device-filter', SDeviceFilter);
